import type * as React from "react";
import { useEffect, useState } from "react";
import { repository } from "~/clientInstance";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { TargetTagMultiSelect } from "~/components/MultiSelect/TargetTagMultiSelect";
import { TargetTagSetSelect } from "~/components/MultiSelect/TargetTagSetSelect";
import type { FocusableComponent } from "~/components/VirtualListWithKeyboard/FocusableComponent";
interface TargetTagSelectorProps {
    // Required
    value: string[] | undefined;
    onChange: (value: string[]) => void;
    // Shared
    items?: string[];
    autoFocus?: boolean;
    // Used by TargetTagSetSelect
    fieldNameFormatter?: (tagSetName: string) => string;
    respectTagSetType?: boolean;
    // Used by TargetTagMultiSelect
    label?: string | JSX.Element;
    canAdd?: boolean;
    error?: string;
    helperText?: string | JSX.Element;
    empty?: React.ReactNode;
    accessibleName?: string;
    analyticsArea?: string;
    openOnFocus?: boolean;
    hideFloatingLabel?: boolean;
    disabled?: boolean;
    validate?(value: string[]): string;
    multiSelectRef?(component: FocusableComponent | null): void;
}
export const TargetTagSelector: React.FC<TargetTagSelectorProps> = (props) => {
    const isTargetTagSetsEnabled = isFeatureToggleEnabled("TargetTagSetsFeatureToggle");
    const [loadedItems, setLoadedItems] = useState<string[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        if (!isTargetTagSetsEnabled && !props.items && !loadedItems && !isLoading) {
            setIsLoading(true);
            repository.MachineRoles.all()
                .then((roles) => {
                setLoadedItems(roles);
                setIsLoading(false);
            })
                .catch(() => {
                setLoadedItems([]);
                setIsLoading(false);
            });
        }
    }, [isTargetTagSetsEnabled, props.items, loadedItems, isLoading]);
    const value = props.value ?? [];
    if (isTargetTagSetsEnabled) {
        const defaultFieldNameFormatter = (tagSetName: string) => `target tags - ${tagSetName}`;
        return <TargetTagSetSelect value={value} onChange={props.onChange} fieldNameFormatter={props.fieldNameFormatter ?? defaultFieldNameFormatter} autoFocus={props.autoFocus} respectTagSetType={props.respectTagSetType} canAdd={props.canAdd}/>;
    }
    const items = props.items ?? loadedItems ?? [];
    return (<TargetTagMultiSelect value={value} onChange={props.onChange} items={items} label={props.label} canAdd={props.canAdd} error={props.error} helperText={props.helperText} validate={props.validate} empty={props.empty} accessibleName={props.accessibleName} analyticsArea={props.analyticsArea} autoFocus={props.autoFocus} openOnFocus={props.openOnFocus} hideFloatingLabel={props.hideFloatingLabel} disabled={props.disabled} multiSelectRef={props.multiSelectRef}/>);
};
TargetTagSelector.displayName = "TargetTagSelector"
